// "Try it" — start a quick, MATTER-LESS test session for a saved agent
// (fn-71 / ADR 0035 / R12).
//
// The fn-71 admin authoring loop ends with "Try it": from the agent detail
// page (which is also where create/edit lands), an admin starts a throwaway
// session of the just-saved agent and chats with it via the ui-react `<Chat>`
// surface (the same end-user component the `/preview` testbed renders).
//
// **fn-63 — matter-less, never faked.** A Try-it session is a no-matter admin
// session: the create input OMITS `matterId` entirely (see
// `buildTrySessionCreate`). The BFF `runs.start` proxy then leaves `matter_id`
// off the CP body, so the CP creates a user/account-scoped session and skips
// the matter-ownership check. We must NEVER synthesize a placeholder matter id
// — the old synthetic-UUID hack 403s `tenancy_matter_not_owned` now that the
// fn-4 matter checker is live (memory `fn63-matter-less-sessions` /
// `smokeball-public-api-rejects-platform-token`).
//
// **Why the headless driver, not the adapter's `startSession`:** creating a
// session triggers the fn-67 bootstrap turn; sending the first message while
// that turn is still running 409s. `bootstrapAndKickoff` (the fn-69.2 driver)
// creates the session, waits out the bootstrap-ack idle, THEN sends the first
// message — the same proven sequence the `/preview` matter-chat start uses.

import { useCallback, useMemo, useRef, useState } from 'react';

import { bffEndpoints } from '../../auth/bff-origin.js';
import {
  type HeadlessCreateInput,
  type HeadlessTransport,
  bootstrapAndKickoff,
  createHeadlessTransport,
  toHeadlessClient,
} from '../../headless/index.js';
import { createVanillaTrpcClient } from '../../trpc-client.js';

export interface TrySessionInput {
  readonly agentId: string;
  readonly agentName: string;
  /** The first message sent to the agent once the bootstrap turn idles. */
  readonly message: string;
}

export type TrySessionStatus = 'idle' | 'starting' | 'started';

export interface UseTrySession {
  readonly status: TrySessionStatus;
  readonly error: string | undefined;
  readonly start: (input: TrySessionInput) => void;
  readonly reset: () => void;
}

/**
 * Build the create input for a Try-it session.
 *
 * CRITICAL (fn-63): the returned object has NO `matterId` key — a Try-it
 * session is matter-less by construction and a matter id must never be
 * faked/synthesized. The `metadata.title` gives the session a readable
 * subject in the conversation list. Pure + exported for unit tests.
 */
export function buildTrySessionCreate(agentId: string, agentName: string): HeadlessCreateInput {
  const trimmed = agentName.trim();
  const title = trimmed.length > 0 ? `Try: ${trimmed}` : 'Try it';
  // No `matterId` — matter-less (fn-63). Do NOT add one here.
  return { agentId, metadata: { title } };
}

/**
 * Drive a Try-it session start. `onStarted(sessionId)` fires once the first
 * message has been delivered; the caller then renders `<Chat sessionId>`.
 * A superseded start (reset / re-submit) is ignored via the monotonic
 * `genRef` token so a stale start can't navigate or surface a late error.
 */
export function useTrySession(onStarted: (sessionId: string) => void): UseTrySession {
  const [status, setStatus] = useState<TrySessionStatus>('idle');
  const [error, setError] = useState<string | undefined>(undefined);
  const genRef = useRef(0);

  const transport = useMemo<HeadlessTransport>(
    () =>
      createHeadlessTransport(
        toHeadlessClient(createVanillaTrpcClient({ trpcUrl: bffEndpoints().trpcUrl })),
      ),
    [],
  );

  const start = useCallback(
    async (input: TrySessionInput): Promise<void> => {
      const gen = ++genRef.current;
      setStatus('starting');
      setError(undefined);

      const create = buildTrySessionCreate(input.agentId, input.agentName);

      try {
        const boot = await bootstrapAndKickoff({ transport, create, kickoff: input.message });
        if (genRef.current !== gen) return;
        if (boot.outcome === 'bootstrap-unavailable') {
          setStatus('idle');
          setError(`The session wasn’t ready to start (${boot.status}). Try again.`);
          return;
        }
        setStatus('started');
        onStarted(boot.sessionId);
      } catch (err) {
        if (genRef.current !== gen) return;
        setStatus('idle');
        setError((err as Error).message ?? 'Couldn’t start the session — try again.');
      }
    },
    [transport, onStarted],
  );

  const reset = useCallback((): void => {
    genRef.current += 1;
    setStatus('idle');
    setError(undefined);
  }, []);

  return {
    status,
    error,
    start: (i) => void start(i),
    reset,
  };
}
